import { useCallback, useMemo } from 'react';
import { Box, Flex, Icon } from '@chakra-ui/react';
import { KInput } from '@/ui-library/components/atoms/KInput';
import { KSelect } from '@/ui-library/components/molecules/KSelect';
import AsyncSelect from 'react-select/async';
import debounce from 'debounce-promise';
import moment from 'moment-timezone';
import { Building07, Phone } from '@kula-ai/icons-core';
import { Gmeet, Teams, Zoom } from '@/assets/icons/location';
import { InterviewDetailsAPI } from '@/pages/KInterviewScheduler/SchedulerDetails/api/InterviewDetailsAPI';
import { SelectWithDescriptionOption } from '@/components/formBuilder/SelectComponents';
import { INTERVIEW_TYPE_OPTIONS, SCHEDULE_AI_COPY } from '../../constants';
import {
  selectStyles,
  COORDINATOR_LOCATION_OPTIONS,
  DURATION_OPTIONS_FROM_PLAN,
} from '../options';
import { KFormLabel, KText } from '@/ui-library/components';
import { UserOption } from '@/components/select';
import { Switch } from '@chakra-ui/react';
import { SchAiInterviewerMultiValueLabel } from './SchAiInterviewerMultiValueLabel';

const COPY = SCHEDULE_AI_COPY;

const requiredAsterisk = (
  <Box as="span" color="red" marginLeft="2px" aria-hidden>
    *
  </Box>
);

const LOCATION_ICON_MAP = {
  google_meet: Gmeet,
  zoom: Zoom,
  teams: Teams,
  phone_interview: Phone,
  onsite: Building07,
};

const LOCATION_OPTIONS_WITH_ICONS = COORDINATOR_LOCATION_OPTIONS.map((opt) => ({
  ...opt,
  icon: LOCATION_ICON_MAP[opt.value],
}));

export const EditableDetails = ({
  interviewName = '',
  setInterviewName,
  interviewKind,
  setInterviewKind,
  selectedInterviewers,
  setSelectedInterviewers,
  durationMinutes,
  setDurationMinutes,
  location,
  setLocation,
  officeId,
  setOfficeId,
  offices = [],
  isOfficeLoading = false,
  calendarVisibility = 'default',
  setCalendarVisibility,
  availableLocations,
  officeAddressFieldRef,
}) => {
  // Dynamic week start for interviewer fetch (next 2 weeks from today); format matches API week_start_date
  const weekStartDate = useMemo(
    () => moment().startOf('week').format('YYYY-MM-DDTHH:mm:ss Z'),
    []
  );

  const fetchInterviewers = useCallback(
    async (inputValue) => {
      try {
        const response = await InterviewDetailsAPI.getInterviewersList({
          startDate: weekStartDate,
          q: inputValue,
        });
        return (
          response?.data?.interviewers?.map((user) => ({
            ...user,
            value: user.id,
            label: user.name,
            name: user.name,
            avatar: user.avatar,
          })) || []
        );
      } catch {
        return [];
      }
    },
    [weekStartDate]
  );

  const debouncedFetch = useMemo(
    () => debounce(fetchInterviewers, 300),
    [fetchInterviewers]
  );

  const locationFormatOptionLabel = useCallback(
    (option) =>
      option?.icon ? (
        <Flex alignItems="center" gap="8px">
          <Icon
            as={option.icon}
            w="16px"
            h="16px"
            color="#6D659D"
            flexShrink={0}
          />
          <span>{option.label}</span>
        </Flex>
      ) : (
        option?.label
      ),
    []
  );

  const locationOptionsFiltered = useMemo(() => {
    const locationsMap = availableLocations?.locations ?? availableLocations;
    if (
      !locationsMap ||
      typeof locationsMap !== 'object' ||
      Object.keys(locationsMap).length === 0
    ) {
      return LOCATION_OPTIONS_WITH_ICONS;
    }
    return LOCATION_OPTIONS_WITH_ICONS.filter(
      (opt) => locationsMap[opt.value] === true
    );
  }, [availableLocations]);

  const officeOptions = useMemo(
    () =>
      (offices ?? []).map((o) => ({
        value: o.id,
        label: o.name,
        desc: o.location,
      })),
    [offices]
  );

  return (
    <Box>
      {setInterviewName && (
        <Box mb="12px">
          <KInput
            label="Interview name"
            value={interviewName}
            onChange={(e) => setInterviewName(e.target.value)}
            placeholder="Enter interview name"
          />
        </Box>
      )}
      <Box mb="12px">
        <Box mb="4px">
          <KFormLabel>Interview type</KFormLabel>
        </Box>
        <Box mt="8px">
          <KSelect
            options={INTERVIEW_TYPE_OPTIONS}
            value={INTERVIEW_TYPE_OPTIONS.find(
              (opt) => opt.value === interviewKind
            )}
            onChange={(opt) => setInterviewKind(opt?.value)}
            placeholder="Select interview type"
          />
        </Box>
      </Box>

      <Box mb="12px">
        <Box mb="4px">
          <KFormLabel>
            Interviewer
            {requiredAsterisk}
          </KFormLabel>
        </Box>
        <Box mt="8px">
          <AsyncSelect
            isMulti
            cacheOptions
            defaultOptions
            isClearable={false}
            loadOptions={debouncedFetch}
            value={selectedInterviewers}
            onChange={(opts) => setSelectedInterviewers(opts || [])}
            placeholder="Add interviewers"
            styles={selectStyles}
            noOptionsMessage={() => 'Type to search interviewers'}
            components={{
              Option: UserOption,
              MultiValueLabel: SchAiInterviewerMultiValueLabel,
            }}
          />
        </Box>
      </Box>

      <Box mb="12px">
        <Box mb="4px">
          <KFormLabel>
            {COPY.duration}
            {requiredAsterisk}
          </KFormLabel>
        </Box>
        <Box mt="8px">
          <KSelect
            options={DURATION_OPTIONS_FROM_PLAN}
            value={
              DURATION_OPTIONS_FROM_PLAN.find(
                (o) => o.value === durationMinutes
              ) || DURATION_OPTIONS_FROM_PLAN.find((o) => o.value === 60)
            }
            onChange={(opt) => opt && setDurationMinutes(opt.value)}
            placeholder="Select duration"
            menuPortalTarget={document.body}
          />
        </Box>
      </Box>

      {setCalendarVisibility && (
        <Box mb="12px">
          <Flex alignItems="center" justifyContent="space-between">
            <Flex flexDir="column" gap="6px">
              <KText fontSize="13px" fontWeight="600" color="#413B74">
                {COPY.makeInterviewPrivate}
              </KText>
              <KText fontSize="13px" color="#6D659D">
                {COPY.hideCalendarFromNonAttendees}
              </KText>
            </Flex>
            <Box flexShrink={0}>
              <Switch
                className={`ats-switch-toogle schedule-ai-private-switch ${calendarVisibility === 'private' ? 'active' : ''}`}
                colorScheme="ats-primary"
                isChecked={calendarVisibility === 'private'}
                onChange={(e) =>
                  setCalendarVisibility(
                    e.target.checked ? 'private' : 'default'
                  )
                }
                cursor="pointer"
              />
            </Box>
          </Flex>
        </Box>
      )}

      <Box mb="12px">
        <Flex alignItems="center" gap="6px" mb="4px">
          <KFormLabel>
            {COPY.location}
            {requiredAsterisk}
          </KFormLabel>
        </Flex>
        <Box mt="8px">
          <KSelect
            options={locationOptionsFiltered}
            value={
              location
                ? locationOptionsFiltered.find((o) => o.value === location) ||
                  null
                : null
            }
            onChange={(opt) => opt && setLocation(opt.value)}
            formatOptionLabel={locationFormatOptionLabel}
            placeholder="Select location"
          />
        </Box>
      </Box>

      {location === 'onsite' && setOfficeId && (
        <Box mb="12px" ref={officeAddressFieldRef}>
          <Box mb="4px">
            <KFormLabel>
              Office Address
              {requiredAsterisk}
            </KFormLabel>
          </Box>
          <Box mt="8px">
            <KSelect
              options={officeOptions}
              value={
                officeId !== null && officeId !== undefined
                  ? officeOptions.find(
                      (o) => Number(o.value) === Number(officeId)
                    ) || null
                  : null
              }
              onChange={(opt) =>
                setOfficeId(
                  opt?.value !== undefined && opt?.value !== null
                    ? Number(opt.value)
                    : null
                )
              }
              placeholder="Select office address"
              isLoading={isOfficeLoading}
              menuPlacement="top"
              menuPortalTarget={document.body}
              components={{
                Option: SelectWithDescriptionOption,
                IndicatorSeparator: () => null,
              }}
              extraProps={{ descKey: 'desc' }}
            />
          </Box>
        </Box>
      )}
    </Box>
  );
};
